import React, { useEffect, useRef, useState } from "react";
import ReactGA from "react-ga4";
import { useCookieConsent } from "../contexts/CookieConsentContext";
import { TargetHitsVisualization } from "./TargetHitsVisualization";
import { HitDto, TargetResultDto } from "../types/TargetScannedDataDTOs";
import { TargetImageData } from "../types/TargetImageData";
import { Button, Col, Container, Modal, Row } from "react-bootstrap";
import "./SessionReport.css"
import "./TargetReportModal.css"
import "./PDFSessionReport.css"
import { calculateGroupCenterPosition, calculateGroupHeight, calculateGroupWidth, calculateMaxSpread, calculateMeanRadius } from "../utils/statsCalculation";
import { calculateTotalScoreSum } from "../utils/calculateScore";
import PointsBarChartComponent from "./PointsBarChart";
import { exportToPdf } from "../utils/exportToPDF";
import { ShootingEventConfig } from "../types/ShootingEventConfig";
import { getTargetImageData } from "../configs/utils/TargetImageConfigFactory";

interface SessionReportProps {
    targetResults: Array<TargetResultDto>;
    dateString: string;
    shotsPerTarget: number;
    hitRadius: number;
    targetOwnerName?: string;
    competitionName?: string;
    competitionDate?: string;
    actionButtons?: (disabled: boolean) => React.ReactNode;
    shootingEventConfig: ShootingEventConfig;
}

const numberOfDecimalPlaces: number = 2;

const formatDate = (dateString: string): string => new Date(dateString).toLocaleDateString('pl-PL');

export function SessionReport(props: SessionReportProps) {
    const shootingEventConfig: ShootingEventConfig = props.shootingEventConfig;

    const [showTargetReportModal, setShowTargetReportModal] = useState<boolean>(false);
    const [targetReportResult, setTargetReportResult] = useState<TargetResultDto>({coordinatesOfHitsRelativeToCenter: [], listOfPointsScored: []});
    const [groupCenterPosition, setGroupCenterPosition] = useState<HitDto>({x: 0, y: 0});
    const [isExportingPdf, setIsExportingPdf] = useState<boolean>(false);

    const pdfViewContainerRef = useRef(null);

    const targetImageData: TargetImageData = getTargetImageData(shootingEventConfig.targetConfig.targetId);
    const baseReportTitle: string = "Podsumowanie sesji";
    const pdfReportTitle: string = props.targetOwnerName
        ? `${baseReportTitle} - ${props.targetOwnerName}`
        : baseReportTitle;
    const baseTargetSummaryTitle: string = "Podsumowanie tarczy";
    const hasCompetitionContext: boolean = Boolean(props.targetOwnerName && props.competitionName && props.competitionDate);
    const reportDetails: string = hasCompetitionContext
        ? `${props.competitionName} ${formatDate(props.competitionDate!)}`
        : formatDate(props.dateString);

    useEffect(() => {
        setGroupCenterPosition(calculateGroupCenterPosition(props.targetResults.flatMap(targetResult => targetResult.coordinatesOfHitsRelativeToCenter)));
    }, []);
    
    const handleCloseTargetReportModal = () => setShowTargetReportModal(false);

    const handleShowTargetReportModal = (targetResult: TargetResultDto) => {
        setTargetReportResult(targetResult);
        setShowTargetReportModal(true);
    }

    const getAllHits = (): HitDto[] => {
        return props.targetResults.map(targetResult => targetResult.coordinatesOfHitsRelativeToCenter).flat();
    };

    const { analyticsConsent } = useCookieConsent();

    const handleExportToPDF = async () => {
        if (isExportingPdf) {
            return;
        }

        if (analyticsConsent === "accepted") {
            ReactGA.event({
                category: "User",
                action: "Exported session report to PDF",
                label: shootingEventConfig.shootingEventName
            });
        }

        setIsExportingPdf(true);
        try {
            await exportToPdf(pdfViewContainerRef);
        } finally {
            setIsExportingPdf(false);
        }
    };

    const renderTitleWithOwner = (mainId: string, ownerId: string, baseTitle: string) => (
        <span id={mainId}>
            <span>{baseTitle}</span>
            {props.targetOwnerName && <span id={ownerId}>{props.targetOwnerName}</span>}
        </span>
    );

    const renderReportHeader = () => (
        <>
            <p id="title">
                {renderTitleWithOwner("titleMain", "titleOwner", baseReportTitle)}
                {!hasCompetitionContext && <span>{reportDetails}</span>}
            </p>
            {hasCompetitionContext && <p id="competitionDetails">{reportDetails}</p>}
        </>
    );

    const renderPdfHeader = () => (
        <>
            <p id="pdfTitle">{pdfReportTitle}{!hasCompetitionContext && <span>{reportDetails}</span>}</p>
            {hasCompetitionContext && <p id="pdfCompetitionDetails">{reportDetails}</p>}
        </>
    );

    return (
        <div>
        <Container fluid id="sessionReportMainContainer">
            <Row>
                <Col md={7} className="mb-2">
                    {renderReportHeader()}
                    <TargetHitsVisualization
                        targetImageData={targetImageData}
                        coordinatesOfHitsRelativeToCenter={getAllHits()}
                        hitRadius={props.hitRadius}
                    />
                </Col>
                <Col md={5}>
                    <div className="d-flex gap-2 mb-2 flex-wrap">
                        {props.actionButtons?.(isExportingPdf)}
                        <Button disabled={isExportingPdf} onClick={handleExportToPDF}>Pobierz PDF</Button>
                        <Button variant="info" onClick={() => alert(infoMessage)}>Info</Button>
                    </div>
                    <div id="totalScore">
                        <p>Suma punktów: <span>{calculateTotalScoreSum(props.targetResults.flatMap(targetResult => targetResult.listOfPointsScored))}</span></p>
                        <p>Ilość trafień/strzałów: <span>{props.targetResults.flatMap(targetResult => targetResult.listOfPointsScored).length}/{props.shotsPerTarget*props.targetResults.length}</span></p>
                    </div>
                    <div style={{height: "20%", width: "100%"}}>
                        <PointsBarChartComponent listOfPointsScored={props.targetResults.flatMap(targetResult => targetResult.listOfPointsScored.map(point => parseFloat(point)))}/>
                    </div>

                    <div id="groupStats">
                        <p id="groupStatsTitle">Analiza skupienia</p>   
                        <p>Przesunięcie w poziomie: <span>{groupCenterPosition.x.toFixed(numberOfDecimalPlaces)} mm</span></p>
                        <p>Przesunięcie w pionie: <span>{groupCenterPosition.y.toFixed(numberOfDecimalPlaces)} mm</span></p>
                        <p>Maksymalny rozrzut: <span>{calculateMaxSpread(getAllHits()).toFixed(numberOfDecimalPlaces)} mm</span></p>
                        <p>Śr. promień: <span>{calculateMeanRadius(getAllHits()).toFixed(numberOfDecimalPlaces)} mm</span></p>
                        <p>Szerokość: <span>{calculateGroupWidth(getAllHits()).toFixed(numberOfDecimalPlaces)} mm</span></p>
                        <p>Wysokość: <span>{calculateGroupHeight(getAllHits()).toFixed(numberOfDecimalPlaces)} mm</span></p>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="d-flex flex-row flex-wrap justify-content-center">
                        {props.targetResults.map((targetResult, index) => {
                            return (
                                <div key={`target-thumbnail-${index}`} className="target-thumbnail" onClick={() => handleShowTargetReportModal(targetResult)}>
                                    <p>{calculateTotalScoreSum(targetResult.listOfPointsScored)}</p>
                                    <TargetHitsVisualization
                                        targetImageData={targetImageData}
                                        coordinatesOfHitsRelativeToCenter={targetResult.coordinatesOfHitsRelativeToCenter}
                                        hitRadius={props.hitRadius}
                                    />
                                </div>
                            )
                        })}
                    </div>
                </Col>
            </Row>

            <Modal show={showTargetReportModal} onHide={handleCloseTargetReportModal} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>{renderTitleWithOwner("modalTitleMain", "modalTitleOwner", baseTargetSummaryTitle)}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Container id="modalContainer">
                        <Row>
                            <Col md={7}>
                                <p>Data: <span>{new Date(props.dateString).toLocaleDateString('pl-PL')}</span></p>
                                <TargetHitsVisualization
                                    targetImageData={targetImageData}
                                    coordinatesOfHitsRelativeToCenter={targetReportResult.coordinatesOfHitsRelativeToCenter}
                                    hitRadius={props.hitRadius}
                                />
                            </Col>
                            <Col md={5}>
                                <div id="modalTotalScore">
                                    <p>Suma punktów: <span>{calculateTotalScoreSum(targetReportResult.listOfPointsScored)}</span></p>
                                    <p>Ilość trafień/strzałów: <span>{targetReportResult.listOfPointsScored.length}/{props.shotsPerTarget}</span></p>
                                </div>
                                <div style={{height: "26%", width: "100%"}}>
                                    <PointsBarChartComponent listOfPointsScored={targetReportResult.listOfPointsScored.map(point => parseFloat(point))}/>
                                </div>
                                <div id="modalGroupStats">
                                    <p>Przesuniecie w poziomie: <span>{calculateGroupCenterPosition(targetReportResult.coordinatesOfHitsRelativeToCenter).x.toFixed(numberOfDecimalPlaces)} mm</span></p>
                                    <p>Przesuniecie w pionie: <span>{calculateGroupCenterPosition(targetReportResult.coordinatesOfHitsRelativeToCenter).y.toFixed(numberOfDecimalPlaces)} mm</span></p>
                                    <p>Maksymalny rozrzut: <span>{calculateMaxSpread(targetReportResult.coordinatesOfHitsRelativeToCenter).toFixed(numberOfDecimalPlaces)} mm</span></p>
                                    <p>Średnia odległość od środka: <span>{calculateMeanRadius(targetReportResult.coordinatesOfHitsRelativeToCenter).toFixed(numberOfDecimalPlaces)} mm</span></p>
                                    <p>Szerokość: <span>{calculateGroupWidth(targetReportResult.coordinatesOfHitsRelativeToCenter).toFixed(numberOfDecimalPlaces)} mm</span></p>
                                    <p>Wysokość: <span>{calculateGroupHeight(targetReportResult.coordinatesOfHitsRelativeToCenter).toFixed(numberOfDecimalPlaces)} mm</span></p>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseTargetReportModal}>
                        Zamknij
                    </Button>
                </Modal.Footer>
            </Modal>

        </Container>
        
        {/* Container for PDF export */}    
        <Container fluid ref={pdfViewContainerRef} className="pdf-view">
            <Row style={{ width: '100%' }}>
                <Col xs={7}>
                    {renderPdfHeader()}
                    <TargetHitsVisualization
                        targetImageData={targetImageData}
                        coordinatesOfHitsRelativeToCenter={getAllHits()}
                        hitRadius={props.hitRadius}
                    />
                </Col>
                <Col xs={5}>
                    <div id="pdfTotalScore">
                        <p>Suma punktów: <span>{calculateTotalScoreSum(props.targetResults.flatMap(targetResult => targetResult.listOfPointsScored))}</span></p>
                        <p>Ilość trafień/strzałów: <span>{props.targetResults.flatMap(targetResult => targetResult.listOfPointsScored).length}/{props.shotsPerTarget*props.targetResults.length}</span></p>
                    </div>
                    <div style={{height: "20%", width: "100%"}}>
                        <PointsBarChartComponent listOfPointsScored={props.targetResults.flatMap(targetResult => targetResult.listOfPointsScored.map(point => parseFloat(point)))}/>
                    </div>

                    <div id="pdfGroupStats">    
                        <p id="groupStatsTitle">Analiza skupienia</p>   
                        <p>Przesunięcie w poziomie: <span>{groupCenterPosition.x.toFixed(numberOfDecimalPlaces)} mm</span></p>
                        <p>Przesunięcie w pionie: <span>{groupCenterPosition.y.toFixed(numberOfDecimalPlaces)} mm</span></p>
                        <p>Maksymalny rozrzut: <span>{calculateMaxSpread(getAllHits()).toFixed(numberOfDecimalPlaces)} mm</span></p>
                        <p>Śr. promień: <span>{calculateMeanRadius(getAllHits()).toFixed(numberOfDecimalPlaces)} mm</span></p>
                        <p>Szerokość: <span>{calculateGroupWidth(getAllHits()).toFixed(numberOfDecimalPlaces)} mm</span></p>
                        <p>Wysokość: <span>{calculateGroupHeight(getAllHits()).toFixed(numberOfDecimalPlaces)} mm</span></p>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="d-flex flex-row flex-wrap justify-content-center">
                        {props.targetResults.map((targetResult, index) => {
                            return (
                                <div key={`pdf-target-thumbnail-${index}`} className="pdf-target-thumbnail">
                                    <p>{calculateTotalScoreSum(targetResult.listOfPointsScored)}</p>
                                    <TargetHitsVisualization
                                        targetImageData={targetImageData}
                                        coordinatesOfHitsRelativeToCenter={targetResult.coordinatesOfHitsRelativeToCenter}
                                        hitRadius={props.hitRadius}
                                    />
                                </div>
                            )
                        })}

                    </div>
                </Col>
            </Row>
        </Container>
        </div>
    )
};

const infoMessage = `Możesz kliknąć w miniatury tarcz umieszczonych na samym dole strony, aby zobaczyć statystyki oddzielnie dla każdej z nich.`;
